// PRD 08 §6 + §8 — code delete confirm. If BE/mock returns
// `CODE_HAS_LINKED_REFERENCES`, swap into a single-button "cannot delete" info
// dialog that shows the linked PRO item + module counts from `error.meta`.

import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';

import { ConfirmModal } from '@/components/feedback/ConfirmModal';
import { toast } from '@/components/feedback/useToast';

import { useDeleteDiseaseCode } from '../hooks/use-disease-code-mutations';
import { useDiseaseCodePageStore } from '../store/disease-code-page.store';
import { DiseaseCodeApiError, type DiseaseCode } from '../types/disease-code.types';
import { getDiseaseCodeErrorMessage } from '../utils/error-message';

interface BlockedState {
  proItemCount: number;
  moduleCount: number;
}

interface DeleteDiseaseCodeConfirmProps {
  code: DiseaseCode | null;
  onClose: () => void;
}

// Single-button informational dialog. `ConfirmModal` always renders two
// buttons, which is wrong UX for a non-destructive notice — so we inline a
// minimal native dialog using the project's modal CSS classes.
function BlockedInfoModal({
  title,
  message,
  closeLabel,
  onClose,
}: {
  title: string;
  message: string;
  closeLabel: string;
  onClose: () => void;
}) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  return createPortal(
    <div
      className="modal-overlay modal-overlay--visible"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-labelledby="dcBlockedTitle"
    >
      <div className="modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal__header">
          <h2 className="modal__title" id="dcBlockedTitle">
            {title}
          </h2>
        </div>
        <div className="modal__body">
          <p className="modal__message" style={{ whiteSpace: 'pre-line' }}>
            {message}
          </p>
        </div>
        <div className="modal__footer">
          <button
            type="button"
            className="btn btn-default btn-primary"
            onClick={onClose}
            autoFocus
          >
            {closeLabel}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}

export function DeleteDiseaseCodeConfirm({ code, onClose }: DeleteDiseaseCodeConfirmProps) {
  const { t } = useTranslation('admin');
  const deleteMutation = useDeleteDiseaseCode();
  const selectedCodeId = useDiseaseCodePageStore((s) => s.selectedCodeId);
  const selectCode = useDiseaseCodePageStore((s) => s.selectCode);

  const [blocked, setBlocked] = useState<BlockedState | null>(null);

  const reset = () => {
    setBlocked(null);
    onClose();
  };

  const handleConfirm = async () => {
    if (!code || deleteMutation.isPending) return;
    try {
      await deleteMutation.mutateAsync({ id: code.id, groupId: code.groupId });
      // Close the reference panel if the deleted code was open in it.
      if (selectedCodeId === code.id) selectCode(null);
      toast.success(t('diseaseCodeManagement.toasts.codeDeleted'));
      reset();
    } catch (e) {
      if (e instanceof DiseaseCodeApiError && e.code === 'CODE_HAS_LINKED_REFERENCES') {
        const meta = e.meta ?? {};
        setBlocked({
          proItemCount: Number(meta.proItemCount ?? 0),
          moduleCount: Number(meta.moduleCount ?? 0),
        });
        return;
      }
      toast.error(
        getDiseaseCodeErrorMessage(e, t, 'diseaseCodeManagement.toasts.deleteError'),
      );
    }
  };

  // Branch 1 — blocked screen (linked references). Single [Close] button.
  if (blocked && code) {
    return (
      <BlockedInfoModal
        title={t('diseaseCodeManagement.deleteCode.blockedTitle')}
        message={t('diseaseCodeManagement.deleteCode.blockedMessage', {
          proItemCount: blocked.proItemCount,
          moduleCount: blocked.moduleCount,
        })}
        closeLabel={t('diseaseCodeManagement.deleteCode.cancel')}
        onClose={reset}
      />
    );
  }

  // Branch 2 — standard delete confirm.
  return (
    <ConfirmModal
      open={code !== null}
      title={t('diseaseCodeManagement.deleteCode.title')}
      message={
        code
          ? t('diseaseCodeManagement.deleteCode.message', {
              code: code.code,
              name: code.name,
            })
          : ''
      }
      confirmLabel={t('diseaseCodeManagement.deleteCode.confirm')}
      cancelLabel={t('diseaseCodeManagement.deleteCode.cancel')}
      confirmVariant="destructive"
      loading={deleteMutation.isPending}
      onConfirm={handleConfirm}
      onCancel={() => {
        if (deleteMutation.isPending) return;
        reset();
      }}
    />
  );
}
